import React from 'react';
import { Text, View } from 'react-native-web';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Box } from '@styleseat/ui';
import Button from './Button';
import CloseIcon from '../../../images/icons/icon-close.svg';
import Icon from './Icon';
import ModalBox from './ModalBox';
import './Modal.module.scss';

/** The available appearance options */
export const APPEARANCE_OPTIONS = [
  'dialog',
  'menu',
  'upper-dialog',
  'splash',
  'fullScreen',
];

/** The available appearance options */
export const THEME_OPTIONS = [
  'white',
  'blue',
  'yellow',
];

/**
 * Determines the amount of padding to supply to the modal style. Early iterations of the Modal
 * defaulted to 16px of padding to several of the appearance styles, so this function is needed to
 * support that legacy usage and allow newer code to compensate.
 * @param {String} appearance One of the `APPEARANCE_OPTIONS`
 * @param {Number} padding The specified padding value
 */
function determinePaddingValue(appearance, padding) {
  let paddingFromAppearance;

  if ([
    'menu',
    'upper-dialog',
    'dialog',
  ].indexOf(appearance) !== -1) {
    paddingFromAppearance = 16;
  } else {
    paddingFromAppearance = 0;
  }

  return typeof padding === 'number' ? padding : paddingFromAppearance;
}

/**
 * @deprecated Use Portal instead
 * A modal with appearance options.
 *
 * This must be rendered within a MenuHost context.
 *
 * See also:
 * ModalTrigger
 * ModalHost
 */
export default function Modal({
  appearance,
  theme,
  title,
  showClose,
  showCloseInBody,
  onCloseRequest,
  children,
  renderLeftAction,
  renderRightAction,
  renderTitle,
  padding,
  testID,
}) {
  const appearanceStyle = appearance && APPEARANCE_OPTIONS.includes(appearance)
    ? appearance
    : 'dialog';

  const themeStyle = theme && THEME_OPTIONS.includes(theme) ? theme : 'white';

  const showCloseButtonInHeader = showClose || showClose === false
    ? showClose
    : typeof onCloseRequest === 'function' && !showCloseInBody;

  // If a render function is not specified for the right toolbar action, render a close button based
  // on relevant props
  const renderRight = typeof renderRightAction === 'function'
    ? renderRightAction
    : () => {
      if (!showCloseButtonInHeader) {
        return null;
      }

      return (
        <React.Fragment>
          <Button
            appearance="none"
            testID="close-modal-btn"
            onClick={onCloseRequest}
            style={{
              position: 'absolute',
              right: '5px',
            }}
          >
            <Box paddingLeft={4} paddingRight={12} testID="close-modal">
              <Icon uri={CloseIcon} size={20} />
            </Box>
          </Button>
          <View style={{ height: 45 }} />
        </React.Fragment>
      );
    };

  // show the header bar if...
  // a. there's a title specified
  const showHeader = typeof title !== 'undefined'
    || typeof renderTitle === 'function'
    // b. there is something to render on the left side
    || typeof renderLeftAction === 'function'
    // c. there is something to render on the right side
    || typeof renderRightAction === 'function'
    // d. there will be a close button in the header
    || showCloseButtonInHeader;

  const modalBodyBorderTop = showHeader
    && (React.Children.count(children) !== 1
      || React.Children.only(children).type !== ModalBox);

  return (
    <View
      styleName={classNames('modal', appearanceStyle, themeStyle)}
      style={{
        padding: determinePaddingValue(appearanceStyle, padding),
      }}
      testID={testID}
    >
      {showHeader && (
        <View styleName="header" testID="modal-header">
          {typeof renderLeftAction === 'function'
            && renderLeftAction(onCloseRequest)}
          {typeof renderTitle === 'function' ? (
            renderTitle({ title })
          ) : (
            <Text styleName="title" testID="modal-title">
              {title || ''}
            </Text>
          )}
          {renderRight(onCloseRequest)}
        </View>
      )}
      <View
        styleName={classNames('modal-body', {
          modalBodyBorderTop,
        })}
        testID="modal-body"
      >
        {showCloseInBody && (
          <Box
            flexDirection="row"
            justifyContent="flex-end"
            paddingTop={12}
            paddingRight={12}
          >
            <Button
              appearance="none"
              testID="close-modal-btn-in-body"
              onClick={onCloseRequest}
            >
              <Box testID="close-modal-in-body">
                <Icon uri={CloseIcon} size={20} />
              </Box>
            </Button>
          </Box>
        )}
        {children}
      </View>
    </View>
  );
}

Modal.propTypes = {
  /** The appearance of the modal
   *  dialog - Standard full-screen modal dialog (default)
   *  menu - Renders at full-width from the bottom of the screen to mimic a menu,
   *         or renders as a dialog if the screen width is larger than the app
   *  upper-dialog - Renders a dialog near the top of the screen
   *  fullScreen - A full screen modal, with a 500px maximum width and height between 300px and
   *  600px on desktop
   *  splash - A different kind of modal */
  appearance: PropTypes.oneOf(APPEARANCE_OPTIONS),
  /** The title of the modal */
  title: PropTypes.string,
  /** Whether or not to show the close button. If not set, the close button will show if
   onCloseRequest is set to a function and showCloseInBody is false */
  showClose: PropTypes.bool,
  /** Whether or not to show the close button within the body of the Modal. */
  showCloseInBody: PropTypes.bool,
  /** Called when the close button is clicked. See ModalTrigger for examples. */
  onCloseRequest: PropTypes.func,
  /** The body of the modal */
  children: PropTypes.node.isRequired,
  /** Design theme of the component  */
  theme: PropTypes.oneOf(THEME_OPTIONS),
  /** Returns a component to render on the left side of the modal header, provided a function to
   * call to close the modal. */
  renderLeftAction: PropTypes.func,
  /** Returns a component to render on the right side of the modal header, provided a function call
   * to close the modal. If this is specified, `showClose` and `onCloseRequest` are ignored. */
  renderRightAction: PropTypes.func,
  /** Returns a component to render on the title of the modal header and is provided with the
   * passed in "title" prop */
  renderTitle: PropTypes.func,
  /**
   * The amount of padding around all contents of the modal
   */
  padding: PropTypes.number,
  /** The testID used for automation and other testing */
  testID: PropTypes.string,
};

Modal.defaultProps = {
  appearance: undefined,
  title: undefined,
  showClose: undefined,
  showCloseInBody: undefined,
  onCloseRequest: undefined,
  theme: undefined,
  renderLeftAction: undefined,
  renderRightAction: undefined,
  renderTitle: undefined,
  padding: null,
  testID: null,
};
